import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { MembersPanel } from '../components/MembersPanel.js';
import { Panel } from '../components/Panel.js';
import {
  Badge,
  Button,
  Card,
  CursorPagination,
  ErrorNote,
  Icon,
  PageHeader,
  PagedTableShell,
  Stat,
  Table,
  Td,
  TextInput,
  buttonClass,
  cx,
  Select,
  useClientPaging,
} from '../components/ui/index.js';
import { formatMoney } from './BillingPage.js';
import { activityIcon } from '../lib/activity-icon.js';
import { downloadCsv, readJson, useRemote } from '../lib/remote.js';
import { useSession } from '../lib/session.js';
import { useAdminLocale } from '../lib/admin-locale.js';

interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

const linkClass = 'font-medium text-blue-600 hover:text-blue-800 dark:hover:text-blue-200';

function BackLink(props: { to: string; label: string }) {
  return (
    <p className="text-sm">
      <Link className={linkClass} to={props.to}>
        ← {props.label}
      </Link>
    </p>
  );
}

export function ConversationsPage() {
  const { text, formatDate } = useAdminLocale();
  const { siteId = '' } = useParams();
  const [cursor, setCursor] = useState<string | null>(null);
  const [locale, setLocale] = useState('');
  const [status, setStatus] = useState('');
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  if (locale) params.set('locale', locale);
  if (status) params.set('status', status);
  const query = params.toString();
  const remote = useRemote<Page<{ id: string; locale: string; status: string; startedAt: string }>>(
    `/v1/sites/${siteId}/conversations${query ? `?${query}` : ''}`,
  );
  const rows = remote.data?.items ?? [];
  return (
    <section className="space-y-6">
      <PageHeader
        description={text(
          'Συνομιλίες επισκεπτών με το bot, με παραπομπές και αξιολογήσεις.',
          'Visitor conversations with the bot, including citations and feedback.',
        )}
        actions={
          <>
            <Select
              value={locale}
              aria-label={text('Γλώσσα', 'Language')}
              className="min-w-40"
              onChange={(next) => {
                setCursor(null);
                setLocale(next);
              }}
              options={[
                { value: '', label: text('Όλες οι γλώσσες', 'All languages') },
                { value: 'el', label: text('Ελληνικά', 'Greek') },
                { value: 'en', label: 'English' },
                { value: 'fr', label: 'Français' },
              ]}
            />
            <Select
              value={status}
              aria-label={text('Κατάσταση', 'Status')}
              className="min-w-40"
              onChange={(next) => {
                setCursor(null);
                setStatus(next);
              }}
              options={[
                { value: '', label: text('Όλες οι καταστάσεις', 'All statuses') },
                { value: 'OPEN', label: text('Ανοιχτές', 'Open') },
                { value: 'CLOSED', label: text('Κλειστές', 'Closed') },
                { value: 'EXPIRED', label: text('Ληγμένες', 'Expired') },
              ]}
            />
          </>
        }
      />
      <BackLink to={`/sites/${siteId}`} label={text('Πίσω στην ιστοσελίδα', 'Back to website')} />
      <Panel
        loading={!remote.data && !remote.error}
        error={remote.error?.message ?? null}
        empty={rows.length === 0}
        emptyLabel={text('Δεν υπάρχουν συνομιλίες.', 'No conversations found.')}
        emptyHint={text(
          'Δοκιμάστε διαφορετικά φίλτρα ή περιμένετε νέες συνομιλίες.',
          'Try different filters or wait for new conversations.',
        )}
      >
        <Card flush>
          <div className="min-h-0 flex-1 overflow-x-auto">
            <Table
              head={[
                text('Έναρξη', 'Started'),
                text('Γλώσσα', 'Language'),
                text('Κατάσταση', 'Status'),
                '',
              ]}
            >
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/70">
                  <Td>
                    <Link className={linkClass} to={`/sites/${siteId}/conversations/${row.id}`}>
                      {formatDate(row.startedAt, true)}
                    </Link>
                  </Td>
                  <Td muted>
                    <span className="uppercase">{row.locale}</span>
                  </Td>
                  <Td>
                    <Badge value={row.status} />
                  </Td>
                  <Td className="text-right">
                    <Link
                      to={`/sites/${siteId}/conversations/${row.id}`}
                      className="inline-flex text-slate-400 hover:text-blue-600 dark:text-slate-500"
                      aria-label={text('Άνοιγμα συνομιλίας', 'Open conversation')}
                    >
                      <Icon name="arrow" className="size-4" />
                    </Link>
                  </Td>
                </tr>
              ))}
            </Table>
          </div>
          <CursorPagination
            hasNext={Boolean(remote.data?.nextCursor)}
            isFirst={!cursor}
            onFirst={() => setCursor(null)}
            onNext={() => setCursor(remote.data?.nextCursor ?? null)}
          />
        </Card>
      </Panel>
    </section>
  );
}

export function TranscriptPage(props: { canWrite: boolean }) {
  const { text } = useAdminLocale();
  const { siteId = '', conversationId = '' } = useParams();
  const navigate = useNavigate();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const remote = useRemote<{
    messages: Array<{
      id: string;
      role: string;
      text: string;
      feedback: string | null;
      citations: Array<{ label: string; url: string | null }>;
    }>;
  }>(`/v1/sites/${siteId}/conversations/${conversationId}`);
  return (
    <section className="space-y-6">
      <PageHeader
        description={text(
          `Αναγνωριστικό ${conversationId.slice(0, 8)}…`,
          `ID ${conversationId.slice(0, 8)}…`,
        )}
        actions={
          props.canWrite ? (
            <Button
              type="button"
              variant="danger"
              onClick={() => {
                setDeleteError(null);
                void readJson(`/v1/sites/${siteId}/conversations/${conversationId}`, {
                  method: 'DELETE',
                })
                  .then(() => void navigate(`/sites/${siteId}/conversations`))
                  .catch((error: unknown) => {
                    setDeleteError(
                      error instanceof Error
                        ? error.message
                        : text('Η διαγραφή απέτυχε', 'Deletion failed'),
                    );
                  });
              }}
            >
              <Icon name="trash" className="size-4" />{' '}
              {text('Διαγραφή συνομιλίας', 'Delete conversation')}
            </Button>
          ) : null
        }
      />
      <BackLink
        to={`/sites/${siteId}/conversations`}
        label={text('Όλες οι συνομιλίες', 'All conversations')}
      />
      {deleteError ? (
        <p className="text-sm text-rose-700 dark:text-rose-300">{deleteError}</p>
      ) : null}
      <Panel
        loading={!remote.data && !remote.error}
        error={remote.error?.message ?? null}
        empty={(remote.data?.messages ?? []).length === 0}
        emptyLabel={text('Κενή συνομιλία.', 'Empty conversation.')}
      >
        <ol className="space-y-4">
          {remote.data?.messages.map((message) => {
            const isUser = message.role === 'USER';
            return (
              <li key={message.id} className={cx('flex', isUser ? 'justify-end' : 'justify-start')}>
                <div
                  className={cx(
                    'max-w-2xl rounded-2xl px-4 py-3 text-sm shadow-sm',
                    isUser
                      ? 'rounded-br-md bg-blue-600 text-white'
                      : 'rounded-bl-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200',
                  )}
                >
                  <p
                    className={cx(
                      'text-[11px] font-semibold tracking-wider uppercase',
                      isUser ? 'text-blue-200' : 'text-slate-400 dark:text-slate-500',
                    )}
                  >
                    {isUser ? text('Επισκέπτης', 'Visitor') : 'Bot'}
                  </p>
                  <p className="mt-1 leading-6 whitespace-pre-wrap">{message.text}</p>
                  {message.citations.length > 0 ? (
                    <ul className="mt-3 flex flex-wrap gap-1.5">
                      {message.citations.map((citation) => (
                        <li key={citation.label}>
                          {citation.url ? (
                            <a
                              href={citation.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-blue-50 dark:bg-blue-950/40 hover:text-blue-700 dark:text-blue-300"
                            >
                              {citation.label}
                            </a>
                          ) : (
                            <span className="inline-flex rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs text-slate-700 dark:text-slate-300">
                              {citation.label}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {message.feedback ? (
                    <p className="mt-2">
                      <Badge value={message.feedback} />
                    </p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </Panel>
    </section>
  );
}

const LEAD_STATUSES = [
  { value: 'NEW', el: 'Νέο', en: 'New' },
  { value: 'IN_PROGRESS', el: 'Σε εξέλιξη', en: 'In progress' },
  { value: 'CLOSED', el: 'Κλειστό', en: 'Closed' },
];

export function LeadsPage(props: { canWrite: boolean }) {
  const { text } = useAdminLocale();
  const { siteId = '' } = useParams();
  const [cursor, setCursor] = useState<string | null>(null);
  const [csvError, setCsvError] = useState<string | null>(null);
  const remote = useRemote<
    Page<{
      id: string;
      name: string;
      email: string;
      status: string;
      marketingOptIn: boolean;
      noticeVersion: string;
      conversationId: string | null;
    }>
  >(`/v1/sites/${siteId}/leads${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`);
  const rows = remote.data?.items ?? [];
  return (
    <section className="space-y-6">
      <PageHeader
        description={text(
          'Στοιχεία επικοινωνίας που άφησαν επισκέπτες μέσω του widget.',
          'Contact details submitted by visitors through the widget.',
        )}
        actions={
          props.canWrite ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setCsvError(null);
                void downloadCsv(`/v1/sites/${siteId}/leads.csv`, 'leads.csv').catch(
                  (error: unknown) => {
                    setCsvError(
                      error instanceof Error
                        ? error.message
                        : text('Αποτυχία CSV', 'CSV download failed'),
                    );
                  },
                );
              }}
            >
              <Icon name="download" className="size-4" /> {text('Λήψη CSV', 'Download CSV')}
            </Button>
          ) : null
        }
      />
      <BackLink to={`/sites/${siteId}`} label={text('Πίσω στην ιστοσελίδα', 'Back to website')} />
      {csvError ? <p className="text-sm text-rose-700 dark:text-rose-300">{csvError}</p> : null}
      <Panel
        loading={!remote.data && !remote.error}
        error={remote.error?.message ?? null}
        empty={rows.length === 0}
        emptyLabel={text('Δεν υπάρχουν ενδιαφέροντα.', 'No leads found.')}
      >
        <Card flush>
          <div className="min-h-0 flex-1 overflow-x-auto">
            <Table
              head={[
                text('Όνομα', 'Name'),
                'Email',
                text('Κατάσταση', 'Status'),
                'Marketing',
                text('Ενημέρωση', 'Notice'),
                text('Συνομιλία', 'Conversation'),
              ]}
            >
              {rows.map((lead) => (
                <tr key={lead.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/70">
                  <Td>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {lead.name}
                    </span>
                  </Td>
                  <Td muted>{lead.email}</Td>
                  <Td>
                    {props.canWrite ? (
                      <Select
                        aria-label={text('Κατάσταση ενδιαφέροντος', 'Lead status')}
                        value={lead.status}
                        onChange={(next) => {
                          void readJson(`/v1/sites/${siteId}/leads/${lead.id}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ status: next }),
                          }).then(() => remote.mutate());
                        }}
                        options={LEAD_STATUSES.map((option) => ({
                          value: option.value,
                          label: text(option.el, option.en),
                        }))}
                      />
                    ) : (
                      <Badge value={lead.status} />
                    )}
                  </Td>
                  <Td muted>{lead.marketingOptIn ? text('Ναι', 'Yes') : text('Όχι', 'No')}</Td>
                  <Td muted>{lead.noticeVersion}</Td>
                  <Td>
                    {lead.conversationId ? (
                      <Link
                        className={linkClass}
                        to={`/sites/${siteId}/conversations/${lead.conversationId}`}
                      >
                        {text('Άνοιγμα', 'Open')}
                      </Link>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500">—</span>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </div>
          <CursorPagination
            hasNext={Boolean(remote.data?.nextCursor)}
            isFirst={!cursor}
            onFirst={() => setCursor(null)}
            onNext={() => setCursor(remote.data?.nextCursor ?? null)}
          />
        </Card>
      </Panel>
    </section>
  );
}

interface UsageSite {
  id: string;
  name: string;
  origin: string;
  conversations: number;
  answers: number;
  estimatedCost: string;
}

interface UsageReport {
  period: { startsAt: string; endsAt: string; timezone: string; year: number; month: number };
  answers: { quota: number; completed: number; reserved: number };
  conversations: number;
  messages: number;
  siteCap: number | null;
  daily: number[];
  sites: UsageSite[];
  estimatedCost: { amount: string; currency: string; uncertainCalls: number };
}

interface ClientUsage {
  tenantId: string;
  name: string;
  plan: string | null;
  answers: { quota: number; completed: number };
  estimatedCost: { amount: string; currency: string };
}

function countLabel(value: number, locale = 'el-GR'): string {
  return new Intl.NumberFormat(locale).format(value);
}

function money(amount: string, currency: string, locale = 'el-GR'): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  const digits = value !== 0 && Math.abs(value) < 0.01 ? 6 : 2;
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: digits,
  }).format(value)} ${currency}`;
}

function hostOf(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
}

function share(used: number, limit: number): number {
  if (limit <= 0) return used > 0 ? 1 : 0;
  return used / limit;
}

function percentLabel(used: number, limit: number): string {
  const ratio = share(used, limit);
  if (ratio > 0 && ratio < 0.01) return '<1%';
  return `${Math.min(100, Math.round(ratio * 100))}%`;
}

function percentWidth(used: number, limit: number): number {
  const ratio = share(used, limit);
  if (ratio <= 0) return 0;
  return Math.min(100, Math.max(1, Math.round(ratio * 100)));
}

function limitStatus(
  used: number,
  limit: number,
  shared: boolean,
): 'WITHIN' | 'NEAR' | 'AT' | 'SHARED' {
  if (shared) return 'SHARED';
  const ratio = share(used, limit);
  if (ratio >= 1) return 'AT';
  if (ratio >= 0.8) return 'NEAR';
  return 'WITHIN';
}

function barClass(used: number, limit: number): string {
  const ratio = share(used, limit);
  if (ratio >= 1) return 'bg-rose-50 dark:bg-rose-950/400';
  if (ratio >= 0.8) return 'bg-amber-50 dark:bg-amber-950/400';
  return 'bg-blue-600';
}

function DailyChart(props: { daily: number[]; month: number }) {
  const { locale, text } = useAdminLocale();
  const intlLocale = locale === 'en' ? 'en-GB' : 'el-GR';
  const values = props.daily;
  const total = values.reduce((sum, value) => sum + value, 0);
  const width = 640;
  const height = 180;
  const max = Math.max(1, ...values);
  const count = Math.max(1, values.length);
  const xAt = (index: number) => (count === 1 ? width / 2 : (index / (count - 1)) * width);
  const yAt = (value: number) => height - 16 - (value / max) * (height - 32);
  const line = values
    .map(
      (value, index) =>
        `${index === 0 ? 'M' : 'L'}${xAt(index).toFixed(1)},${yAt(value).toFixed(1)}`,
    )
    .join(' ');
  const area = `${line} L${xAt(count - 1).toFixed(1)},${height - 16} L${xAt(0).toFixed(1)},${height - 16} Z`;
  const marks = [0, Math.floor((count - 1) / 2), count - 1].filter(
    (index, position, all) => all.indexOf(index) === position,
  );
  const month = new Intl.DateTimeFormat(intlLocale, { month: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2024, props.month - 1, 1)),
  );
  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-48 w-full"
        role="img"
        aria-label={text(`${total} συνομιλίες μέχρι σήμερα`, `${total} conversations to date`)}
      >
        <line x1="0" y1={height - 16} x2={width} y2={height - 16} stroke="#e2e8f0" />
        <path d={area} fill="#dbeafe" />
        <path d={line} fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinejoin="round" />
        {values.map((value, index) =>
          value > 0 ? (
            <circle key={index} cx={xAt(index)} cy={yAt(value)} r="4" fill="#2563eb" />
          ) : null,
        )}
      </svg>
      <div className="mt-2 flex justify-between text-xs text-slate-400 dark:text-slate-500">
        {marks.map((index) => (
          <span key={index}>
            {index + 1} {month}
          </span>
        ))}
      </div>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
        {total === 0
          ? text('Καμία συνομιλία αυτόν τον μήνα.', 'No conversations this month.')
          : text(
              `${countLabel(total, intlLocale)} συνομιλίες από την 1η μέχρι σήμερα.`,
              `${countLabel(total, intlLocale)} conversations from the 1st through today.`,
            )}
      </p>
    </div>
  );
}

export function UsagePage(props: { platform: boolean }) {
  const { locale, text, formatDate: localeDate } = useAdminLocale();
  const intlLocale = locale === 'en' ? 'en-GB' : 'el-GR';
  const count = (value: number) => countLabel(value, intlLocale);
  const remote = useRemote<UsageReport>('/v1/usage');
  const clients = useRemote<{ items: ClientUsage[] }>(
    props.platform ? '/v1/platform/usage' : null,
    null,
  );
  const subscription = useRemote<{ status: string; plan: { code: string; name: string } }>(
    '/v1/subscription',
  );
  const usage = remote.data;
  const quota = usage?.answers.quota ?? 0;
  const completed = usage?.answers.completed ?? 0;
  const siteCap = usage?.siteCap ?? null;
  const plan = subscription.data?.plan.name ?? subscription.data?.plan.code;
  const sitesPaging = useClientPaging(usage?.sites ?? []);
  const clientsPaging = useClientPaging(clients.data?.items ?? []);
  return (
    <section className="space-y-6">
      <PageHeader
        description={text(
          'Κατανάλωση ανά ιστοσελίδα, μηνιαίο όριο και εκτίμηση κόστους παρόχου. Τα ποσά δεν είναι τιμολόγιο πελάτη.',
          'Usage by website, monthly limit, and estimated provider cost. These amounts are not customer invoices.',
        )}
        badge={plan ? <Badge value={subscription.data?.status} label={plan} /> : undefined}
      />
      <Panel
        loading={!remote.data && !remote.error}
        error={remote.error?.message ?? null}
        empty={!remote.data}
        emptyLabel={text('Δεν υπάρχει περίοδος.', 'No usage period is available.')}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            icon="chat"
            accent
            label={text('Απαντήσεις', 'Answers')}
            meter={percentWidth(completed, quota)}
            value={`${count(completed)} / ${count(quota)}`}
            hint={text(
              `${percentLabel(completed, quota)} του μηνιαίου ορίου · κρατημένες ${count(usage?.answers.reserved ?? 0)}`,
              `${percentLabel(completed, quota)} of monthly limit · reserved ${count(usage?.answers.reserved ?? 0)}`,
            )}
          />
          <Stat
            icon="inbox"
            label={text('Συνομιλίες', 'Conversations')}
            value={count(usage?.conversations ?? 0)}
            hint={text(
              `${localeDate(usage?.period.startsAt)} – σήμερα · ${usage?.period.timezone ?? ''}`,
              `${localeDate(usage?.period.startsAt)} – today · ${usage?.period.timezone ?? ''}`,
            )}
          />
          <Stat
            icon="mail"
            label={text('Μηνύματα', 'Messages')}
            value={count(usage?.messages ?? 0)}
            hint={text(
              'Μηνύματα επισκεπτών και απαντήσεων αυτού του μήνα',
              'Visitor messages and answers this month',
            )}
          />
          <Stat
            icon="chart"
            label={text('Κόστος AI', 'AI cost')}
            value={
              usage
                ? money(usage.estimatedCost.amount, usage.estimatedCost.currency, intlLocale)
                : '—'
            }
            hint={
              usage && usage.estimatedCost.uncertainCalls > 0
                ? text(
                    `${usage.estimatedCost.uncertainCalls} κλήσεις με άγνωστη χρέωση`,
                    `${usage.estimatedCost.uncertainCalls} calls with unknown pricing`,
                  )
                : text('Εκτίμηση παρόχου, όχι τιμολόγιο', 'Provider estimate, not an invoice')
            }
          />
        </div>
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <Card
            className="lg:col-span-2"
            title={text('Συνομιλίες ανά ημέρα', 'Conversations by day')}
            description={text(
              'Πραγματικές συνομιλίες του τρέχοντος μήνα, μέχρι σήμερα.',
              'Actual conversations for the current month through today.',
            )}
          >
            <DailyChart daily={usage?.daily ?? []} month={usage?.period.month ?? 1} />
          </Card>
          <Card
            title={text('Όρια ανά ιστοσελίδα', 'Limits by website')}
            description={
              siteCap === null
                ? text(
                    'Κοινό όριο οργανισμού. Δεν υπάρχει ξεχωριστό όριο ανά ιστοσελίδα.',
                    'Shared organization limit. There is no separate per-website limit.',
                  )
                : text(
                    `Ξεχωριστό όριο ${count(siteCap)} απαντήσεων ανά ιστοσελίδα.`,
                    `Separate limit of ${count(siteCap)} answers per website.`,
                  )
            }
          >
            {usage && usage.sites.length > 0 ? (
              <div className="space-y-4">
                {usage.sites.map((site) => {
                  const limit = siteCap ?? quota;
                  return (
                    <div key={site.id}>
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="truncate font-medium text-slate-800 dark:text-slate-200">
                          {hostOf(site.origin)}
                        </span>
                        <span className="shrink-0 text-slate-500 dark:text-slate-400 dark:text-slate-500">
                          {percentLabel(site.answers, limit)}
                        </span>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                        <div
                          className={cx('h-full rounded-full', barClass(site.answers, limit))}
                          style={{ width: `${percentWidth(site.answers, limit)}%` }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                        {text(
                          `${count(site.answers)} / ${count(limit)} απαντήσεις`,
                          `${count(site.answers)} / ${count(limit)} answers`,
                        )}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
                {text('Δεν υπάρχουν ιστοσελίδες.', 'No websites found.')}
              </p>
            )}
          </Card>
        </div>
        <Card title={text('Χρήση ανά ιστοσελίδα', 'Usage by website')} className="mt-6" flush>
          <PagedTableShell pagination={sitesPaging.total > 0 ? sitesPaging.pager : null}>
            <Table
              head={[
                text('Ιστοσελίδα', 'Website'),
                text('Συνομιλίες', 'Conversations'),
                text('Απαντήσεις', 'Answers'),
                text('Όριο', 'Limit'),
                text('Κόστος AI', 'AI cost'),
                text('Κατάσταση', 'Status'),
              ]}
            >
              {sitesPaging.rows.map((site) => {
                const limit = siteCap ?? quota;
                return (
                  <tr key={site.id}>
                    <Td>
                      <Link className={linkClass} to={`/conversations?site=${site.id}`}>
                        {hostOf(site.origin)}
                      </Link>
                      <p className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                        {site.name}
                      </p>
                    </Td>
                    <Td>{count(site.conversations)}</Td>
                    <Td>{count(site.answers)}</Td>
                    <Td>{count(limit)}</Td>
                    <Td>
                      {money(
                        site.estimatedCost,
                        usage?.estimatedCost.currency ?? 'USD',
                        intlLocale,
                      )}
                    </Td>
                    <Td>
                      <Badge value={limitStatus(site.answers, limit, siteCap === null)} />
                    </Td>
                  </tr>
                );
              })}
            </Table>
          </PagedTableShell>
        </Card>
      </Panel>
      {props.platform ? (
        <Card
          title={text('Εκτιμώμενο κόστος ανά πελάτη', 'Estimated cost by customer')}
          description={text(
            'Άθροισμα εκτιμήσεων παρόχου για τον τρέχοντα μήνα κάθε πελάτη. Δεν εκδίδεται τιμολόγιο.',
            'Sum of provider estimates for each customer’s current month. No invoice is issued.',
          )}
          flush
        >
          {clients.error ? <ErrorNote message={clients.error.message} /> : null}
          <PagedTableShell pagination={clientsPaging.total > 0 ? clientsPaging.pager : null}>
            <Table
              head={[
                text('Πελάτης', 'Customer'),
                text('Πακέτο', 'Plan'),
                text('Απαντήσεις', 'Answers'),
                text('Όριο', 'Limit'),
                text('Κόστος AI', 'AI cost'),
                text('Κατάσταση', 'Status'),
              ]}
            >
              {clientsPaging.rows.map((item) => (
                <tr key={item.tenantId}>
                  <Td>{item.name}</Td>
                  <Td>{item.plan ?? '—'}</Td>
                  <Td>{count(item.answers.completed)}</Td>
                  <Td>{count(item.answers.quota)}</Td>
                  <Td>
                    {money(item.estimatedCost.amount, item.estimatedCost.currency, intlLocale)}
                  </Td>
                  <Td>
                    {item.plan ? (
                      <Badge
                        value={limitStatus(item.answers.completed, item.answers.quota, false)}
                      />
                    ) : (
                      <Badge value="SHARED" label={text('Χωρίς πακέτο', 'No plan')} />
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </PagedTableShell>
        </Card>
      ) : null}
    </section>
  );
}

export function MembersPage(props: { canWrite: boolean }) {
  const { text } = useAdminLocale();
  const { user } = useSession();
  return (
    <section className="space-y-6">
      <PageHeader
        description={
          props.canWrite
            ? text(
                'Λογαριασμοί με πρόσβαση στον οργανισμό. Κάθε μέλος βλέπει μόνο αυτόν τον οργανισμό.',
                'Accounts with access to the organization. Each member sees only this organization.',
              )
            : text(
                'Ο ρόλος προβολής δεν προσκαλεί ούτε αφαιρεί μέλη.',
                'The viewer role cannot invite or remove members.',
              )
        }
      />
      <MembersPanel canWrite={props.canWrite} currentUserId={user?.id} />
    </section>
  );
}

type AuditPeriod = 'all' | 'today' | 'week' | 'month';

type AuditCategory =
  | 'all'
  | 'members'
  | 'sites'
  | 'sync'
  | 'sources'
  | 'bot'
  | 'leads'
  | 'billing'
  | 'security'
  | 'other';

interface AuditRow {
  id: string;
  action: string;
  objectType: string;
  objectId: string | null;
  actorId: string | null;
  createdAt: string;
}

const AUDIT_CATEGORIES: ReadonlyArray<{ value: AuditCategory; el: string; en: string }> = [
  { value: 'all', el: 'Όλες οι κατηγορίες', en: 'All categories' },
  { value: 'members', el: 'Χρήστες', en: 'Users' },
  { value: 'sites', el: 'Ιστοσελίδες', en: 'Websites' },
  { value: 'sync', el: 'Συγχρονισμός', en: 'Synchronization' },
  { value: 'sources', el: 'Πηγές γνώσης', en: 'Knowledge sources' },
  { value: 'bot', el: 'Bot / συνομιλίες', en: 'Bot / conversations' },
  { value: 'leads', el: 'Επαφές', en: 'Contacts' },
  { value: 'billing', el: 'Πακέτα', en: 'Plans' },
  { value: 'security', el: 'Ασφάλεια / σύνδεση', en: 'Security / connection' },
  { value: 'other', el: 'Άλλα', en: 'Other' },
];

function auditCategory(action: string): Exclude<AuditCategory, 'all'> {
  if (action.startsWith('member.') || action.startsWith('auth.')) return 'members';
  if (action.startsWith('site.') || action.startsWith('tenant.') || action.startsWith('origin.')) {
    return 'sites';
  }
  if (action.startsWith('sync.')) return 'sync';
  if (action.startsWith('source.') || action.startsWith('knowledge.')) return 'sources';
  if (
    action.startsWith('bot.') ||
    action.startsWith('preview.') ||
    action.startsWith('conversation.') ||
    action.startsWith('widget.')
  ) {
    return 'bot';
  }
  if (action.startsWith('lead.')) return 'leads';
  if (action.startsWith('subscription.') || action.startsWith('plan.')) return 'billing';
  if (
    action.startsWith('pairing.') ||
    action.startsWith('installation.') ||
    action.includes('revoked') ||
    action.includes('deleted')
  ) {
    return 'security';
  }
  return 'other';
}

function auditPeriodStart(period: AuditPeriod): Date | null {
  const now = new Date();
  if (period === 'today') return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (period === 'week') return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  if (period === 'month') return new Date(now.getFullYear(), now.getMonth(), 1);
  return null;
}

export function AuditPage() {
  const { text, formatDate: localeDate } = useAdminLocale();
  const remote = useRemote<AuditRow[]>('/v1/audit');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<AuditCategory>('all');
  const [period, setPeriod] = useState<AuditPeriod>('month');
  const [objectType, setObjectType] = useState('all');

  const objectTypes = useMemo(() => {
    const set = new Set<string>();
    for (const row of remote.data ?? []) {
      if (row.objectType) set.add(row.objectType);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [remote.data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const from = auditPeriodStart(period);
    return (remote.data ?? []).filter((row) => {
      if (category !== 'all' && auditCategory(row.action) !== category) return false;
      if (objectType !== 'all' && row.objectType !== objectType) return false;
      if (from && new Date(row.createdAt).getTime() < from.getTime()) return false;
      if (!q) return true;
      const haystack = `${row.action} ${row.objectType} ${row.objectId ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [remote.data, query, category, period, objectType]);

  const paging = useClientPaging(filtered, {
    resetKey: `${query}|${category}|${period}|${objectType}`,
  });
  const rows = paging.rows;

  return (
    <section className="space-y-6">
      <PageHeader
        description={text(
          'Καταγραφή ενεργειών διαχείρισης στον οργανισμό.',
          'Log of administrative actions in the organization.',
        )}
      />
      <div className="flex flex-col gap-3 admin-surface p-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <Icon
            name="file"
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400 dark:text-slate-500"
          />
          <TextInput
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={text('Αναζήτηση ενέργειας…', 'Search actions…')}
            aria-label={text('Αναζήτηση audit', 'Search audit log')}
            flush
            className="pl-9"
          />
        </div>
        <Select
          aria-label={text('Κατηγορία', 'Category')}
          className="w-full shrink-0 sm:w-52"
          value={category}
          onChange={(next) => setCategory(next as AuditCategory)}
          options={AUDIT_CATEGORIES.map((item) => ({
            value: item.value,
            label: text(item.el, item.en),
          }))}
        />
        <Select
          aria-label={text('Τύπος αντικειμένου', 'Object type')}
          className="w-full shrink-0 sm:w-48"
          value={objectType}
          onChange={setObjectType}
          options={[
            { value: 'all', label: text('Όλοι οι τύποι', 'All types') },
            ...objectTypes.map((type) => ({ value: type, label: type })),
          ]}
        />
        <Select
          aria-label={text('Περίοδος', 'Period')}
          className="w-full shrink-0 sm:w-44"
          value={period}
          onChange={(next) => setPeriod(next as AuditPeriod)}
          options={[
            { value: 'today', label: text('Σήμερα', 'Today') },
            { value: 'week', label: text('Τελευταίες 7 ημέρες', 'Last 7 days') },
            { value: 'month', label: text('Αυτός ο μήνας', 'This month') },
            { value: 'all', label: text('Όλες οι ημερομηνίες', 'All dates') },
          ]}
        />
      </div>
      <Panel
        loading={!remote.data && !remote.error}
        error={remote.error?.message ?? null}
        empty={paging.total === 0}
        emptyLabel={
          (remote.data?.length ?? 0) > 0
            ? text('Καμία εγγραφή με αυτά τα φίλτρα.', 'No entries match these filters.')
            : text('Κενό αρχείο.', 'The audit log is empty.')
        }
      >
        <Card flush>
          <PagedTableShell pagination={paging.total > 0 ? paging.pager : null}>
            <Table
              head={[
                text('Ενέργεια', 'Action'),
                text('Αντικείμενο', 'Object'),
                text('Χρόνος', 'Time'),
              ]}
            >
              {rows.map((row) => {
                const icon = activityIcon(row.action);
                return (
                  <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/70">
                    <Td>
                      <span className="flex items-center gap-3">
                        <span
                          className={`grid size-9 shrink-0 place-items-center rounded-full ${icon.tone}`}
                        >
                          <Icon name={icon.name} className="size-4" />
                        </span>
                        <code className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs text-slate-800 dark:bg-slate-800 dark:text-slate-200">
                          {row.action}
                        </code>
                      </span>
                    </Td>
                    <Td muted>
                      <span className="font-mono text-xs">
                        {row.objectType}
                        {row.objectId ? (
                          <span className="text-slate-400 dark:text-slate-500">
                            {' '}
                            · {row.objectId.slice(0, 8)}
                          </span>
                        ) : null}
                      </span>
                    </Td>
                    <Td muted>{localeDate(row.createdAt, true)}</Td>
                  </tr>
                );
              })}
            </Table>
          </PagedTableShell>
        </Card>
      </Panel>
    </section>
  );
}

interface PlatformOverview {
  tenants: number;
  activeSites: number;
  failedVersions: number;
  conversations: number;
  leads: number;
  locales: { el: number; en: number; fr: number };
  month: { year: number; month: number; daily: number[] };
  estimatedCostUsd: string;
  costLabel: string;
  collectedCents?: number;
}

const ANSWER_LIMIT_RATIO = 0.8;
const HEARTBEAT_STALE_MS = 2 * 24 * 60 * 60 * 1000;

type PlatformTenant = {
  id: string;
  name: string;
  status?: string;
  owedCents?: number;
  invoiceCount?: number;
  lastInvoicedAt?: string | null;
  lastPaidAt?: string | null;
  monthlyAnswers?: { completed: number; quota: number };
};

type PlatformSite = {
  id: string;
  name: string;
  canonicalOrigin: string;
  status: string;
  lastHeartbeatAt?: string | null;
  connection?: 'connected' | 'revoked' | 'none';
  tenant: { id: string; name: string };
};

function answerRatio(tenant: PlatformTenant): number {
  const quota = tenant.monthlyAnswers?.quota ?? 0;
  if (quota <= 0) return 0;
  return (tenant.monthlyAnswers?.completed ?? 0) / quota;
}

function offlineReason(
  site: PlatformSite,
  now: number,
  text: (el: string, en: string) => string,
): string | null {
  if (site.status !== 'ACTIVE') return null;
  if (site.connection === 'revoked') return text('Ελέγξτε σύνδεση', 'Check connection');
  if (site.connection === 'none') return text('Χωρίς σύνδεση', 'No connection');
  if (!site.lastHeartbeatAt) return text('Χωρίς επικοινωνία', 'No communication');
  if (now - new Date(site.lastHeartbeatAt).getTime() > HEARTBEAT_STALE_MS)
    return text('Χωρίς επικοινωνία', 'No communication');
  return null;
}

const PLATFORM_LOCALES = [
  { code: 'el' as const, el: 'Ελληνικά', en: 'Greek' },
  { code: 'en' as const, el: 'English', en: 'English' },
  { code: 'fr' as const, el: 'Français', en: 'French' },
];

function ConversationChart(props: { daily: number[] }) {
  const { text } = useAdminLocale();
  const values = props.daily;
  const total = values.reduce((sum, value) => sum + value, 0);
  if (values.length === 0 || total === 0) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
        {text('Δεν υπάρχουν συνομιλίες αυτόν τον μήνα.', 'No conversations this month.')}
      </p>
    );
  }
  const width = 640;
  const height = 148;
  const max = Math.max(1, ...values);
  const count = values.length;
  const xAt = (index: number) => (count === 1 ? width / 2 : (index / (count - 1)) * width);
  const yAt = (value: number) => height - 12 - (value / max) * (height - 24);
  const line = values
    .map(
      (value, index) =>
        `${index === 0 ? 'M' : 'L'}${xAt(index).toFixed(1)},${yAt(value).toFixed(1)}`,
    )
    .join(' ');
  const area = `${line} L${xAt(count - 1).toFixed(1)},${height} L${xAt(0).toFixed(1)},${height} Z`;
  const marks = [0, Math.floor((count - 1) / 2), count - 1];
  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-40 w-full"
        role="img"
        aria-label={text(`${total} συνομιλίες αυτόν τον μήνα`, `${total} conversations this month`)}
      >
        <line x1="0" y1={height - 12} x2={width} y2={height - 12} stroke="#e2e8f0" />
        <path d={area} fill="#dbeafe" />
        <path d={line} fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinejoin="round" />
        {values.map((value, index) =>
          value > 0 ? (
            <circle key={index} cx={xAt(index)} cy={yAt(value)} r="4" fill="#2563eb" />
          ) : null,
        )}
      </svg>
      <div className="mt-2 flex justify-between text-xs text-slate-400 dark:text-slate-500">
        {marks.map((index) => (
          <span key={index}>{index + 1}</span>
        ))}
      </div>
    </div>
  );
}

export function PlatformPage() {
  const { locale, text, formatDate: localeDate } = useAdminLocale();
  const intlLocale = locale === 'en' ? 'en-GB' : 'el-GR';
  const remote = useRemote<PlatformOverview>('/v1/platform/overview', null);
  const tenants = useRemote<PlatformTenant[]>('/v1/tenants', null);
  const sites = useRemote<PlatformSite[]>('/v1/platform/sites', null);
  const debtors = [...(tenants.data ?? [])]
    .filter((tenant) => (tenant.owedCents ?? 0) > 0)
    .sort((a, b) => (b.owedCents ?? 0) - (a.owedCents ?? 0));
  const debtorsPaging = useClientPaging(debtors);
  const owedTotal = debtors.reduce((sum, tenant) => sum + (tenant.owedCents ?? 0), 0);
  const invoiceTotal = debtors.reduce((sum, tenant) => sum + (tenant.invoiceCount ?? 0), 0);
  const nearLimit = [...(tenants.data ?? [])]
    .filter((tenant) => answerRatio(tenant) >= ANSWER_LIMIT_RATIO)
    .sort((a, b) => answerRatio(b) - answerRatio(a));
  const nearPaging = useClientPaging(nearLimit, { resetKey: 'near-limit' });
  const suspended = (tenants.data ?? []).filter((tenant) => tenant.status === 'SUSPENDED');
  const offline = (sites.data ?? []).flatMap((site) => {
    const reason = offlineReason(site, Date.now(), text);
    return reason ? [{ ...site, reason }] : [];
  });
  const offlinePaging = useClientPaging(offline, { resetKey: 'offline' });
  const data = remote.data;
  const localeTotal = data ? data.locales.el + data.locales.en + data.locales.fr : 0;
  const monthLabel = data
    ? new Date(Date.UTC(data.month.year, data.month.month - 1, 1)).toLocaleDateString(intlLocale, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : '';
  return (
    <section className="space-y-6">
      <PageHeader
        description={text('Συνολική εικόνα όλων των οργανισμών.', 'Overview of all organizations.')}
        actions={
          <Link to="/platform/tenants" className={buttonClass('primary')}>
            <Icon name="building" className="size-4" /> {text('Πελάτες', 'Customers')}
          </Link>
        }
      />
      {remote.error ? <ErrorNote message={remote.error.message} /> : null}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          icon="globe"
          label={text('Ενεργές ιστοσελίδες', 'Active websites')}
          value={data ? data.activeSites : '—'}
          hint={
            data
              ? text(`${data.tenants} οργανισμοί`, `${data.tenants} organizations`)
              : text('Φόρτωση…', 'Loading…')
          }
        />
        <Stat
          icon="chat"
          label={text('Συνομιλίες', 'Conversations')}
          value={data ? data.conversations : '—'}
          hint={
            data
              ? text(
                  `${data.month.daily.reduce((sum, value) => sum + value, 0)} αυτόν τον μήνα`,
                  `${data.month.daily.reduce((sum, value) => sum + value, 0)} this month`,
                )
              : text('Φόρτωση…', 'Loading…')
          }
        />
        <Stat
          icon="users"
          label={text('Επαφές', 'Contacts')}
          value={data ? data.leads : '—'}
          hint={text('Ενδιαφέροντα από όλα τα website', 'Leads from all websites')}
        />
        <Stat
          icon="chart"
          label={text('Εκτίμηση κόστους', 'Cost estimate')}
          value={data ? `${data.estimatedCostUsd} USD` : '—'}
          hint={
            data && data.failedVersions > 0
              ? text(
                  `${data.failedVersions} αποτυχημένες εκδόσεις`,
                  `${data.failedVersions} failed versions`,
                )
              : text('Εκτίμηση, όχι τιμολόγιο', 'Estimate, not an invoice')
          }
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          icon="alert"
          label={text('Χρωστάνε', 'Outstanding')}
          value={tenants.data ? formatMoney(owedTotal, 'EUR', intlLocale) : '—'}
          hint={
            tenants.data
              ? text(
                  `${invoiceTotal.toLocaleString(intlLocale)} ανοιχτά τιμολόγια`,
                  `${invoiceTotal.toLocaleString(intlLocale)} open invoices`,
                )
              : text('Φόρτωση…', 'Loading…')
          }
        />
        <Stat
          icon="check"
          label={text('Εισπράξεις μήνα', 'Payments this month')}
          value={
            data && typeof data.collectedCents === 'number'
              ? formatMoney(data.collectedCents, 'EUR', intlLocale)
              : '—'
          }
          hint={text('Πληρωμές που καταχωρίστηκαν αυτόν τον μήνα', 'Payments recorded this month')}
        />
        <Stat
          icon="file"
          label={text('Αποτυχημένες πηγές', 'Failed sources')}
          value={data ? data.failedVersions.toLocaleString(intlLocale) : '—'}
          hint={text(
            'Εκδόσεις γνώσης που δεν ολοκληρώθηκαν',
            'Knowledge versions that did not complete',
          )}
        />
        <Stat
          icon="ban"
          label={text('Σε αναστολή', 'Suspended')}
          value={tenants.data ? suspended.length.toLocaleString(intlLocale) : '—'}
          hint={text('Πελάτες με σταματημένο widget', 'Customers with a stopped widget')}
        />
      </div>

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <Card
          title={text('Συνομιλίες', 'Conversations')}
          description={
            monthLabel
              ? text(`Ημερήσια, ${monthLabel} (UTC).`, `Daily, ${monthLabel} (UTC).`)
              : text('Ημερήσια κατανομή.', 'Daily distribution.')
          }
        >
          {!data && !remote.error ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
              {text('Φόρτωση…', 'Loading…')}
            </p>
          ) : data ? (
            <ConversationChart daily={data.month.daily} />
          ) : null}
        </Card>
        <Card
          title={text('Γλώσσες', 'Languages')}
          description={text(
            'Μερίδιο συνομιλιών σε όλη την πλατφόρμα.',
            'Share of conversations across the platform.',
          )}
        >
          {remote.error ? (
            <ErrorNote message={remote.error.message} />
          ) : !data ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
              {text('Φόρτωση…', 'Loading…')}
            </p>
          ) : localeTotal === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
              {text('Δεν υπάρχουν συνομιλίες.', 'No conversations found.')}
            </p>
          ) : (
            <ul className="space-y-4">
              {PLATFORM_LOCALES.map((language) => {
                const count = data.locales[language.code];
                const percent = Math.round((count / localeTotal) * 100);
                return (
                  <li key={language.code}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-700 dark:text-slate-300">
                        {text(language.el, language.en)}
                      </span>
                      <span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">
                        {percent}%
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                      <div
                        className="h-full rounded-full bg-blue-600"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid items-start gap-6 xl:grid-cols-2">
        <Panel
          loading={!tenants.data && !tenants.error}
          error={tenants.error?.message ?? null}
          empty={nearLimit.length === 0}
          emptyLabel={text(
            'Κανένας πελάτης δεν πλησιάζει το όριο.',
            'No customers are approaching the limit.',
          )}
          emptyHint={text(
            'Εμφανίζονται όσοι έχουν χρησιμοποιήσει τουλάχιστον 80% των μηνιαίων απαντήσεων.',
            'Customers who have used at least 80% of their monthly answers appear here.',
          )}
        >
          <Card
            title={text('Κοντά στο όριο απαντήσεων', 'Near the answer limit')}
            description={text(
              'Τουλάχιστον 80% του μηνιαίου ορίου.',
              'At least 80% of the monthly limit.',
            )}
            flush
          >
            <PagedTableShell pagination={nearPaging.total > 0 ? nearPaging.pager : null}>
              <Table
                head={[
                  text('Πελάτης', 'Customer'),
                  text('Απαντήσεις', 'Answers'),
                  text('Ποσοστό', 'Percentage'),
                ]}
              >
                {nearPaging.rows.map((tenant) => {
                  const completed = tenant.monthlyAnswers?.completed ?? 0;
                  const quota = tenant.monthlyAnswers?.quota ?? 0;
                  const percent = Math.round(answerRatio(tenant) * 100);
                  return (
                    <tr key={tenant.id}>
                      <Td>
                        <Link className={linkClass} to={`/platform/tenants/${tenant.id}`}>
                          {tenant.name}
                        </Link>
                      </Td>
                      <Td muted>
                        {completed.toLocaleString(intlLocale)} / {quota.toLocaleString(intlLocale)}
                      </Td>
                      <Td className="font-medium">{percent.toLocaleString(intlLocale)}%</Td>
                    </tr>
                  );
                })}
              </Table>
            </PagedTableShell>
          </Card>
        </Panel>
        <Panel
          loading={!sites.data && !sites.error}
          error={sites.error?.message ?? null}
          empty={offline.length === 0}
          emptyLabel={text(
            'Όλες οι ενεργές ιστοσελίδες επικοινωνούν.',
            'All active websites are communicating.',
          )}
          emptyHint={text(
            'Φαίνονται όσες δεν έχουν σύνδεση WordPress ή δεν έχουν στείλει σήμα τις τελευταίες 2 μέρες.',
            'Websites without a WordPress connection or a heartbeat in the last 2 days appear here.',
          )}
        >
          <Card
            title={text('Ιστοσελίδες χωρίς σύνδεση', 'Disconnected websites')}
            description={text(
              'Χωρίς WordPress ή χωρίς επικοινωνία τις τελευταίες 2 μέρες.',
              'Without WordPress or communication in the last 2 days.',
            )}
            flush
          >
            <PagedTableShell pagination={offlinePaging.total > 0 ? offlinePaging.pager : null}>
              <Table
                head={[
                  text('Ιστοσελίδα', 'Website'),
                  text('Πελάτης', 'Customer'),
                  text('Κατάσταση', 'Status'),
                  text('Τελευταία επικοινωνία', 'Last communication'),
                ]}
              >
                {offlinePaging.rows.map((site) => (
                  <tr key={site.id}>
                    <Td>
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        {site.name}
                      </span>
                      <span className="mt-0.5 block text-xs text-slate-500">
                        {site.canonicalOrigin}
                      </span>
                    </Td>
                    <Td>
                      <Link className={linkClass} to={`/platform/tenants/${site.tenant.id}`}>
                        {site.tenant.name}
                      </Link>
                    </Td>
                    <Td muted>{site.reason}</Td>
                    <Td muted>{localeDate(site.lastHeartbeatAt, true)}</Td>
                  </tr>
                ))}
              </Table>
            </PagedTableShell>
          </Card>
        </Panel>
      </div>

      {suspended.length > 0 ? (
        <Card
          title={text('Πελάτες σε αναστολή', 'Suspended customers')}
          description={text(
            'Το widget και το pairing είναι σταματημένα.',
            'The widget and pairing are stopped.',
          )}
        >
          <ul className="space-y-2 text-sm">
            {suspended.map((tenant) => (
              <li key={tenant.id}>
                <Link className={linkClass} to={`/platform/tenants/${tenant.id}`}>
                  {tenant.name}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Panel
        loading={!tenants.data && !tenants.error}
        error={tenants.error?.message ?? null}
        empty={debtors.length === 0}
        emptyLabel={text(
          'Κανένας πελάτης δεν χρωστάει.',
          'No customers have an outstanding balance.',
        )}
        emptyHint={text('Τα απλήρωτα υπόλοιπα εμφανίζονται εδώ.', 'Unpaid balances appear here.')}
      >
        <Card
          title={text('Πελάτες που χρωστάνε', 'Customers with outstanding balances')}
          description={text(
            'Υπόλοιπο από τιμολόγια που δεν έχουν εξοφληθεί.',
            'Balance from invoices that have not been paid in full.',
          )}
          flush
        >
          <PagedTableShell pagination={debtorsPaging.total > 0 ? debtorsPaging.pager : null}>
            <Table
              head={[
                text('Πελάτης', 'Customer'),
                text('Αριθμός τιμολογίων', 'Invoice count'),
                text('Τελευταία τιμολόγηση', 'Last invoiced'),
                text('Τελευταία πληρωμή', 'Last payment'),
                text('Χρωστάει', 'Outstanding'),
              ]}
              foot={
                <tr>
                  <Td className="whitespace-nowrap">
                    {text('Σύνολο', 'Total')} ·{' '}
                    {debtors.length === 1
                      ? text('1 πελάτης', '1 customer')
                      : text(
                          `${debtors.length.toLocaleString(intlLocale)} πελάτες`,
                          `${debtors.length.toLocaleString(intlLocale)} customers`,
                        )}
                  </Td>
                  <Td>{invoiceTotal.toLocaleString(intlLocale)}</Td>
                  <Td />
                  <Td />
                  <Td className="font-semibold text-rose-700 dark:text-rose-300">
                    {formatMoney(owedTotal, 'EUR', intlLocale)}
                  </Td>
                </tr>
              }
            >
              {debtorsPaging.rows.map((tenant) => (
                <tr key={tenant.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                  <Td>
                    <Link className={linkClass} to={`/platform/tenants/${tenant.id}`}>
                      {tenant.name}
                    </Link>
                  </Td>
                  <Td muted>{(tenant.invoiceCount ?? 0).toLocaleString(intlLocale)}</Td>
                  <Td muted>{localeDate(tenant.lastInvoicedAt)}</Td>
                  <Td muted>{localeDate(tenant.lastPaidAt)}</Td>
                  <Td className="font-medium text-rose-700 dark:text-rose-300">
                    {formatMoney(tenant.owedCents ?? 0, 'EUR', intlLocale)}
                  </Td>
                </tr>
              ))}
            </Table>
          </PagedTableShell>
        </Card>
      </Panel>
    </section>
  );
}
